import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

export const WHITE_LABEL_STORAGE_KEY = "is_white_label";

const WHITE_LABEL_PARAM = "label";
const WHITE_LABEL_VALUE = "b2b";
const ROBOTS_META_ID = "white-label-robots";
const BLANK_FAVICON =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Crect width='16' height='16' fill='%23ffffff'/%3E%3C/svg%3E";
const BRAND_NAMES = ["HEI CREATIVE", "Hei Creative", "巨能创意"];

export function isWhiteLabelPersisted() {
    try {
        return window.sessionStorage.getItem(WHITE_LABEL_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

function persistWhiteLabel() {
    try {
        window.sessionStorage.setItem(WHITE_LABEL_STORAGE_KEY, "true");
    } catch {
        // Storage can be unavailable in private browsing; the URL flag still works.
    }
}

export function hasWhiteLabelParam(search = "") {
    try {
        return new URLSearchParams(search).get(WHITE_LABEL_PARAM) === WHITE_LABEL_VALUE;
    } catch {
        return false;
    }
}

export function checkWhiteLabelMode(hash = window.location.hash) {
    const queryIndex = hash.indexOf("?");
    const search = queryIndex >= 0 ? hash.slice(queryIndex) : "";
    return hasWhiteLabelParam(search) || isWhiteLabelPersisted();
}

export function removeBrandFromTitle(title = "") {
    const withoutBrand = BRAND_NAMES.reduce(
        (current, brand) => current.split(brand).join(""),
        title
    );

    return withoutBrand
        .replace(/\s*[|·•–—-]\s*/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
}

function sanitizeDocumentTitle() {
    const cleaned = removeBrandFromTitle(document.title);
    if (cleaned !== document.title) {
        document.title = cleaned;
    }
}

function ensureRobotsMeta() {
    let meta = document.getElementById(ROBOTS_META_ID);
    if (!meta) {
        meta = document.createElement("meta");
        meta.id = ROBOTS_META_ID;
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
    }
}

function removeRobotsMeta() {
    document.getElementById(ROBOTS_META_ID)?.remove();
}

function replaceFavicon() {
    document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"]').forEach((icon) => {
        if (!icon.dataset.originalHref) {
            icon.dataset.originalHref = icon.getAttribute("href") || "";
        }
        icon.setAttribute("href", BLANK_FAVICON);
    });
}

function restoreFavicon() {
    document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"]').forEach((icon) => {
        if (icon.dataset.originalHref) {
            icon.setAttribute("href", icon.dataset.originalHref);
            delete icon.dataset.originalHref;
        }
    });
}

const WhiteLabelContext = createContext({ isWhiteLabel: false });

export function WhiteLabelProvider({ children }) {
    const location = useLocation();
    const [isWhiteLabel, setIsWhiteLabel] = useState(
        () => checkWhiteLabelMode(window.location.hash) || hasWhiteLabelParam(window.location.search)
    );

    useEffect(() => {
        const hasParam = hasWhiteLabelParam(location.search);
        if (hasParam) {
            persistWhiteLabel();
        }
        setIsWhiteLabel(hasParam || isWhiteLabelPersisted());
    }, [location.search]);

    useEffect(() => {
        if (!isWhiteLabel) {
            removeRobotsMeta();
            restoreFavicon();
            return undefined;
        }

        ensureRobotsMeta();
        replaceFavicon();
        sanitizeDocumentTitle();

        const observer = new MutationObserver(sanitizeDocumentTitle);
        const titleNode = document.querySelector("title");
        if (titleNode) {
            observer.observe(titleNode, {
                childList: true,
                characterData: true,
                subtree: true,
            });
        }

        return () => observer.disconnect();
    }, [isWhiteLabel]);

    const value = useMemo(() => ({ isWhiteLabel }), [isWhiteLabel]);

    return <WhiteLabelContext.Provider value={value}>{children}</WhiteLabelContext.Provider>;
}

export function useWhiteLabel() {
    const context = useContext(WhiteLabelContext);
    if (!context) {
        throw new Error("useWhiteLabel must be used within WhiteLabelProvider");
    }
    return context;
}
